import { ReactNode } from 'react';

import { PlayerMicCheck } from '~/modules/elements/volume-indicator';
import styles from '~/modules/game-engine/drawing/styles';
import { SlotState } from '~/routes/select-input/slot-state';

interface Props {
  slot: SlotState;
  /** Lit while any control inside the card holds the keyboard, so the card reads as the focused one. */
  focused?: boolean;
  /**
   * Who is singing into this slot, as a control of the caller's own. Its own labelled block rather
   * than another button in the action row: it answers a different question from the input pickers -
   * one is which microphone, the other is which person - and a row of two pickers and a ✕ leaves
   * neither of them wide enough to read a name in.
   */
  singer?: ReactNode;
  /** Tighter type and spacing, for screens that show more than four cards (two rows of four). */
  compact?: boolean;
  children?: ReactNode;
}

/**
 * One player's slot: their colour, what is behind it, and a live level bar. Also what F14's slot
 * picker offers when a profile is bound to no slot, so it takes its actions as children rather than
 * drawing the setup screen's own buttons.
 */
export default function PlayerSlotCard({ slot, focused = false, singer, compact = false, children }: Props) {
  const colour = styles.colors.players[slot.number]?.text ?? 'white';

  return (
    <div
      data-test={`mic-check-p${slot.number}`}
      data-empty={!slot.hasInput}
      className={`flex flex-1 flex-col rounded-xl border bg-black/45 ${compact ? 'gap-2 p-3.5' : 'gap-4 p-[1.375rem]'} ${
        slot.hasInput ? 'border-white/10' : 'border-dashed border-white/10 opacity-55'
      } ${
        // A ring rather than a thicker border: growing the border from 1px to 2px on focus moves
        // everything inside the card by a pixel as the keyboard passes through it.
        focused ? 'shadow-[0_0_0_2px_#facc15,0_0_24px_rgba(250,204,21,.35)]' : ''
      }`}>
      <div className="flex items-center gap-3">
        <span className={`${compact ? 'h-7 w-2' : 'h-10 w-3'} rounded`} style={{ background: colour }} />
        <span
          className={`stroke-text typography font-bold uppercase ${compact ? 'text-xl' : 'text-2xl'}`}
          style={{ color: colour }}>
          Player {slot.number + 1}
        </span>
      </div>

      <div className={`flex flex-col gap-[0.1875rem] ${compact ? 'min-h-11' : 'min-h-15'}`}>
        <span className={`ph-no-capture leading-tight font-bold ${compact ? 'truncate text-xl' : 'text-[1.625rem]'}`}>
          {slot.title}
        </span>
        <span className={`text-white/50 ${compact ? 'truncate text-[0.9375rem]' : 'text-[1.0625rem]'}`}>
          {slot.subtitle}
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        {/* The bar is self-explanatory once there are eight of them; the label is the first thing to go. */}
        {!compact && <span className="text-[0.9375rem] tracking-[0.08em] text-white/45 uppercase">Level</span>}
        <div className={`relative overflow-hidden rounded-[0.4375rem] bg-white/12 ${compact ? 'h-2.5' : 'h-3.5'}`}>
          {slot.hasInput && <PlayerMicCheck playerNumber={slot.number} className="rounded-[0.4375rem]" />}
        </div>
        <span className={`text-white/45 ${compact ? 'truncate text-sm' : 'text-[0.9375rem]'}`}>{slot.hint}</span>
      </div>

      {singer && (
        <div className={`flex flex-col ${compact ? 'gap-1' : 'gap-1.5'}`}>
          <span className={`tracking-[0.08em] text-white/45 uppercase ${compact ? 'text-xs' : 'text-[0.9375rem]'}`}>
            Sings as
          </span>
          {singer}
        </div>
      )}

      {children && <div className="mt-auto flex gap-2">{children}</div>}
    </div>
  );
}
